import React, {memo, useEffect, useRef, useState} from 'react';
import {keyframes} from '@emotion/react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import MUIContainer from '@/components/MUI-Components/MUI-Container';
import DisplayImage from '../display-image';
import {borderRadius, colors, fontSize, fontWeight, lineHeight, overlay} from '@/styles/tokens';
import {sectionHeadingSx} from '@/styles/sectionHeading';

export interface MvpType {
    id: string;
    title: string;
    /** Callout copy under `calloutLabel`. Supply this or `points`. */
    bestSuitedFor?: string;
    /** Short capability list shown in the callout instead of `bestSuitedFor` copy. */
    points?: string[];
    /** Optional paragraph under the callout; omitted when the callout carries the detail. */
    description?: string;
    image: string | StaticImageData;
}

interface MvpTypesProps {
    heading: string;
    subheading: string;
    eyebrow: string;
    types: MvpType[];
    /** Label above the `bestSuitedFor` callout. */
    calloutLabel?: string;
    /** Desktop only: show this many tabs, then scroll the rest of the list. */
    maxVisibleTabs?: number;
    /** `center` (default): image between the tabs and a wide detail card. `right`: the
     *  detail card sits beside the tabs, and the image takes the wide right column with
     *  no caption; points render as individual pills. */
    imagePosition?: 'center' | 'right';
    /** next/image re-encode quality. Raise it when the source is already a compressed
     *  WebP, so a second lossy pass at the default 75 doesn't soften it further. */
    imageQuality?: number;
}

const panelSx = {
    bgcolor: colors.primary.deep,
    borderRadius: borderRadius.xl,
    p: {xs: 2.5, md: 5}
} as const;

// Heading uses the canonical section-heading scale (`sectionHeadingSx`), which carries
// the shared step-down on laptop-class screens (≤1499px) — same as every section that
// renders through `SectionHeader`. Only colour and spacing are section-specific.
const headingSx = [
    sectionHeadingSx,
    {
        color: colors.text.inverse,
        mb: 1.5
    }
] as const;

const subheadingSx = {color: colors.text.inverse, lineHeight: 1.8, mb: {xs: 3, md: 4}, '& a': {color: 'inherit'}} as const;

const gridItemSx = {display: 'flex'} as const;

// Below 600px the image and the side detail card drop out; the active type's details
// open inline under its tab instead. With the detail card (and so the progress bar that
// drives autoplay) hidden, the rotation also stops, so an open item never moves away
// from someone reading it.
const wideOnlyGridItemSx = {display: {xs: 'none', sm: 'flex'}} as const;

const inlineDetailSx = {
    display: {xs: 'block', sm: 'none'},
    px: 1.5,
    pt: 1.5,
    pb: 1
} as const;

const tabListSx = {
    width: '100%',
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    p: 1.5,
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5
} as const;

// Server-side guess at one tab's height plus the list gap, used until the real tabs are
// measured (a two-line title makes a tab taller than this). Same approach as MvpTechStack.
const TAB_HEIGHT_ESTIMATE = 52;
const LIST_PADDING_PX = 12;

const scrollListSx = (maxHeight: number) =>
    ({
        ...tabListSx,
        // The tabs' offsetTop is measured against this box, so it must be positioned.
        position: 'relative',
        // Scrolls only beside the image and detail card. On phones the active type's
        // details open inside the list, and a capped height there would cut them off.
        maxHeight: {md: maxHeight},
        overflowY: {md: 'auto'},
        scrollbarWidth: 'thin',
        scrollbarColor: `${colors.text.muted} transparent`,
        '&::-webkit-scrollbar': {width: 6},
        '&::-webkit-scrollbar-thumb': {bgcolor: colors.text.muted, borderRadius: borderRadius.sm}
    } as const);

// Positioned so the `right` layout's list can fill it; a flex box so the list still
// stretches to the column in the default layout.
const listWrapSx = {position: 'relative', width: '100%', display: 'flex'} as const;

// `right` layout: the list takes the row's height, set by the image, instead of a
// measured 7-tab cap, so tabs, detail card and image line up. Taken out of flow on
// desktop so its own 12 tabs can't stretch the row, and scrolls inside. On phones it
// stays in flow, where the active type's details open inline.

const rightListSx = {
    ...tabListSx,
    position: {xs: 'relative', md: 'absolute'},
    inset: {md: 0},
    overflowY: {md: 'auto'},
    scrollbarWidth: 'thin',
    scrollbarColor: `${colors.text.muted} transparent`,
    '&::-webkit-scrollbar': {width: 6},
    '&::-webkit-scrollbar-thumb': {bgcolor: colors.text.muted, borderRadius: borderRadius.sm}
} as const;

const tabSx = (active: boolean) =>
    ({
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        width: '100%',
        px: 1.5,
        py: 1.25,
        border: 'none',
        cursor: 'pointer',
        textAlign: 'left',
        borderRadius: borderRadius.md,
        bgcolor: active ? colors.primary.deep : 'transparent',
        transition: 'background-color 0.2s ease',
        // The label's colour follows the fill at the same pace, so white text never
        // shows on the white list mid-fade.
        '& .MuiTypography-root': {transition: 'color 0.2s ease'}
    } as const);

const tabNumberSx = (active: boolean) =>
    ({
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 28,
        height: 28,
        flexShrink: 0,
        borderRadius: borderRadius.md,
        fontSize: fontSize[12],
        fontWeight: fontWeight.bold,
        bgcolor: active ? colors.accent.orange : colors.background.subtle,
        color: active ? colors.text.inverse : colors.text.primary
    } as const);

const tabLabelSx = (active: boolean) =>
    ({
        fontSize: fontSize[14],
        fontWeight: active ? fontWeight.bold : fontWeight.medium,
        color: active ? colors.text.inverse : colors.text.primary
    } as const);

// The frame is tall and narrow, so `cover` scales a 4:3 image by the frame's height and it
// renders far wider than its column (~510px at the 340px+ desktop height). The hint has to
// ask for that rendered width, not the column's 25vw, or smaller desktops get a file half
// the size it is drawn at, and it blurs.
const IMAGE_SIZES = '(max-width: 900px) 100vw, 560px';

const imageCardSx = {
    position: 'relative',
    width: '100%',
    minHeight: {xs: 240, md: 340},
    borderRadius: borderRadius.lg,
    overflow: 'hidden'
} as const;

// `right` layout: the frame is at least the artwork's own 5:3 ratio tall (the
// padding-top spacer is 60% of the width), and stretches with the row when the detail
// card runs taller, as it does on laptop widths, so all three boxes stay one height.
// `cover` then trims only the little extra width.
const imageCardRightSx = {
    ...imageCardSx,
    minHeight: 0,
    alignSelf: 'stretch',
    '&::before': {content: '""', display: 'block', pt: '60%'}
} as const;

const imageScrimSx = {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(0deg, ${overlay.dark80} 0%, ${overlay.dark45} 40%, transparent 70%)`
} as const;

const imageCaptionSx = {position: 'absolute', left: 20, right: 20, bottom: 20} as const;

const imageEyebrowSx = {
    color: colors.accent.orange,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    mb: 0.5
} as const;

const imageTitleSx = {color: colors.text.inverse, fontSize: fontSize[24], fontWeight: fontWeight.bold, lineHeight: 1.2} as const;

const detailCardSx = {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    bgcolor: colors.background.default,
    borderRadius: borderRadius.lg,
    p: {xs: 2.5, md: 3.5}
} as const;

// Tab panels: the active one's wrapper drops out of layout (its children sit in the card as before); the rest
// are not displayed but stay in the server HTML.
const shownPanelSx = {display: 'contents'} as const;
const hiddenPanelSx = {display: 'none'} as const;

const detailNumberSx = {color: colors.primary.link, fontSize: fontSize[14], fontWeight: fontWeight.bold, mb: 0.5} as const;

const detailTitleSx = {
    color: colors.text.primary,
    fontSize: {xs: fontSize[24], md: fontSize[30]},
    fontWeight: fontWeight.bold,
    mb: 2.5
} as const;

// The `right` layout's detail card is a narrow column, so its title steps down a size.
const narrowDetailTitleSx = {...detailTitleSx, fontSize: {xs: fontSize[20], md: fontSize[24]}, lineHeight: lineHeight[32]} as const;

// The accent is a clipped child rather than a `borderLeft`: a left border on a rounded box
// squares off the two left corners, so the bar and the card read as different shapes. As an
// inset bar under `overflow: hidden` it is cut by the card's own radius and follows the curve.
const calloutSx = {
    position: 'relative',
    bgcolor: colors.background.subtle,
    borderRadius: borderRadius.md,
    overflow: 'hidden',
    p: 2,
    pl: 2.5,
    mb: 2.5,
    '&::before': {
        content: '""',
        position: 'absolute',
        left: 0,
        top: 0,
        bottom: 0,
        width: 4,
        bgcolor: colors.accent.orange
    }
} as const;

const calloutLabelSx = {
    color: colors.text.primary,
    fontSize: fontSize[12],
    fontWeight: fontWeight.bold,
    letterSpacing: '0.06em',
    textTransform: 'uppercase' as const,
    mb: 1
} as const;

const calloutTextSx = {color: colors.text.primary, fontSize: fontSize[14], lineHeight: 1.7} as const;

const pointListSx = {listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1} as const;

const pointItemSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    color: colors.text.primary,
    fontSize: fontSize[14],
    lineHeight: 1.7,
    '&::before': {
        content: '""',
        width: 6,
        height: 6,
        flexShrink: 0,
        borderRadius: borderRadius.circle,
        bgcolor: colors.accent.orange
    }
} as const;

// `imagePosition="right"`: each point is its own pill rather than a line in a callout.
const pillListSx = {listStyle: 'none', m: 0, p: 0, mb: 3, display: 'flex', flexDirection: 'column', gap: 1.5} as const;

const pillItemSx = {
    ...pointItemSx,
    px: 1.5,
    py: 1,
    bgcolor: colors.background.subtle,
    border: `1px solid ${colors.accent.blueBorder}`,
    borderRadius: borderRadius.md
} as const;

// From `sm` (where both show) the detail card follows the tabs, ahead of the image, so
// the stacked tablet layout keeps the selected type's points next to its tab.
const narrowDetailItemSx = {...wideOnlyGridItemSx, order: {sm: 2}} as const;
const wideImageItemSx = {...wideOnlyGridItemSx, order: {sm: 3}} as const;

const detailDescSx = {color: colors.text.primary, fontSize: fontSize[14], lineHeight: 1.8, mb: 3} as const;

const progressTrackSx = {
    mt: 'auto',
    width: '100%',
    height: 4,
    borderRadius: borderRadius.full,
    bgcolor: colors.background.subtle,
    overflow: 'hidden'
} as const;

// How long each type stays on screen before the section advances itself.
const AUTOPLAY_MS = 6000;

// Declared through Emotion rather than as an `@keyframes` key inside `sx`: an sx-nested
// keyframes block is not hoisted to a real rule, so the animation name resolves to nothing
// and the bar sits frozen at `scaleX(0)` — which also means `animationend` never fires.
const progressGrow = keyframes({
    from: {transform: 'scaleX(0)'},
    to: {transform: 'scaleX(1)'}
});

// The bar is the clock, not a readout of one: the section advances on this animation's
// `animationend`, so the fill and the rotation can never drift apart, and pausing the
// animation pauses the rotation with it. Reduced-motion falls back to a static step fill.
const progressFillSx = (animated: boolean, paused: boolean, percent: number) =>
    ({
        width: '100%',
        height: '100%',
        borderRadius: borderRadius.full,
        bgcolor: colors.accent.orange,
        transformOrigin: 'left center',
        transform: animated ? 'scaleX(0)' : `scaleX(${percent / 100})`,
        ...(animated && {
            animation: `${progressGrow} ${AUTOPLAY_MS}ms linear forwards`,
            animationPlayState: paused ? 'paused' : 'running'
        })
    } as const);

const renderPills = (points: string[]) => (
    <Box component="ul" sx={pillListSx}>
        {points.map((point) => (
            <Box component="li" key={point} className="font-regular-text" sx={pillItemSx}>
                {point}
            </Box>
        ))}
    </Box>
);

const renderDetailBody = (type: MvpType, calloutLabel: string, pills = false) =>
    pills && type.points?.length ? (
        renderPills(type.points)
    ) : (
        <>
            <Box sx={calloutSx}>
                <Typography component="p" className="font-headers" sx={calloutLabelSx}>
                    {calloutLabel}
                </Typography>
                {type.bestSuitedFor && (
                    <Typography component="p" className="font-regular-text" sx={calloutTextSx}>
                        {parse(type.bestSuitedFor)}
                    </Typography>
                )}
                {type.points && type.points.length > 0 && (
                    <Box component="ul" sx={pointListSx}>
                        {type.points.map((point) => (
                            <Box component="li" key={point} className="font-regular-text" sx={pointItemSx}>
                                {point}
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>

            {type.description && (
                <Typography component="p" className="font-regular-text" sx={detailDescSx}>
                    {type.description}
                </Typography>
            )}
        </>
    );

const MvpTypes = ({
    heading,
    subheading,
    eyebrow,
    types,
    calloutLabel = 'Best Suited For',
    maxVisibleTabs,
    imagePosition = 'center',
    imageQuality
}: MvpTypesProps) => {
    const imageRight = imagePosition === 'right';
    const listRef = useRef<HTMLDivElement>(null);
    const scrolls = (!!maxVisibleTabs && types.length > maxVisibleTabs) || imagePosition === 'right';
    const [listMaxHeight, setListMaxHeight] = useState((maxVisibleTabs ?? 0) * TAB_HEIGHT_ESTIMATE + LIST_PADDING_PX * 2);

    // Cap the list at the bottom edge of the Nth tab, re-measured when the list resizes.
    useEffect(() => {
        const list = listRef.current;
        if (!scrolls || !list || !maxVisibleTabs) return undefined;
        const measure = () => {
            const tabs = list.querySelectorAll<HTMLElement>(':scope > button');
            const last = tabs[maxVisibleTabs - 1];
            if (last) setListMaxHeight(last.offsetTop + last.offsetHeight + LIST_PADDING_PX);
        };
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(list);
        return () => observer.disconnect();
    }, [scrolls, maxVisibleTabs]);

    const [activeId, setActiveId] = useState(types?.[0]?.id);
    const [paused, setPaused] = useState(false);
    // Starts off so the server and the first client render agree; the effect turns it on
    // after mount, and only when the visitor has not asked for reduced motion.
    const [autoplay, setAutoplay] = useState(false);
    const activeIndex = Math.max(
        0,
        types.findIndex((type) => type.id === activeId)
    );
    const activeType = types[activeIndex];

    useEffect(() => {
        if (typeof globalThis.window === 'undefined' || !globalThis.matchMedia) {
            return undefined;
        }
        const query = globalThis.matchMedia('(prefers-reduced-motion: reduce)');
        const sync = () => setAutoplay(!query.matches && types.length > 1);
        sync();
        query.addEventListener('change', sync);
        return () => query.removeEventListener('change', sync);
    }, [types.length]);

    const showNext = () => setActiveId(types[(activeIndex + 1) % types.length].id);

    // Autoplay walks past the visible tabs, so keep the active one in view. Scrolls the
    // list itself, never the page (scrollIntoView would drag the window along with it).
    useEffect(() => {
        const list = listRef.current;
        if (!scrolls || !list) return;
        const tab = list.querySelectorAll<HTMLElement>(':scope > button')[activeIndex];
        if (!tab) return;
        const top = tab.offsetTop - LIST_PADDING_PX;
        const bottom = tab.offsetTop + tab.offsetHeight + LIST_PADDING_PX;
        if (top < list.scrollTop) list.scrollTo({top, behavior: 'smooth'});
        else if (bottom > list.scrollTop + list.clientHeight) list.scrollTo({top: bottom - list.clientHeight, behavior: 'smooth'});
    }, [activeIndex, scrolls]);

    return (
        <Box>
            <MUIContainer>
                <Box sx={panelSx}>
                    <Typography variant="h1" component="h2" className="font-headers" sx={headingSx}>
                        {heading}
                    </Typography>
                    <Typography variant="subtitle1" component="p" className="font-regular-text" sx={subheadingSx}>
                        {parse(subheading)}
                    </Typography>

                    {/* Pointer or keyboard attention holds the current type in place — the
                        section should never move out from under someone reading it. */}
                    <Grid
                        container
                        spacing={3}
                        onMouseEnter={() => setPaused(true)}
                        onMouseLeave={() => setPaused(false)}
                        onFocus={() => setPaused(true)}
                        onBlur={() => setPaused(false)}
                    >
                        <Grid item xs={12} md={3} sx={gridItemSx}>
                            <Box sx={listWrapSx}>
                                <Box ref={listRef} sx={imageRight ? rightListSx : scrolls ? scrollListSx(listMaxHeight) : tabListSx}>
                                    {types.map((type, idx) => {
                                        const active = type.id === activeType?.id;
                                        return (
                                            <React.Fragment key={type.id}>
                                                <Box
                                                    component="button"
                                                    type="button"
                                                    onClick={() => setActiveId(type.id)}
                                                    aria-pressed={active}
                                                    sx={tabSx(active)}
                                                >
                                                    <Typography component="span" className="font-headers" sx={tabNumberSx(active)}>
                                                        {String(idx + 1).padStart(2, '0')}
                                                    </Typography>
                                                    <Typography component="span" className="font-regular-text" sx={tabLabelSx(active)}>
                                                        {type.title}
                                                    </Typography>
                                                </Box>
                                                {active && (
                                                    <Box sx={inlineDetailSx}>{renderDetailBody(type, calloutLabel, imageRight)}</Box>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={imageRight ? 6 : 3} sx={imageRight ? wideImageItemSx : wideOnlyGridItemSx}>
                            <Box sx={imageRight ? imageCardRightSx : imageCardSx}>
                                {activeType && (
                                    <DisplayImage
                                        src={activeType.image}
                                        alt={activeType.title}
                                        fill
                                        sizes={IMAGE_SIZES}
                                        quality={imageQuality}
                                        style={{objectFit: 'cover'}}
                                    />
                                )}
                                {!imageRight && (
                                    <>
                                        <Box sx={imageScrimSx} />
                                        <Box sx={imageCaptionSx}>
                                            <Typography component="p" className="font-headers" sx={imageEyebrowSx}>
                                                {eyebrow}
                                            </Typography>
                                            <Typography component="p" className="font-headers" sx={imageTitleSx}>
                                                {activeType?.title}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </Grid>

                        <Grid item xs={12} md={imageRight ? 3 : 6} sx={imageRight ? narrowDetailItemSx : wideOnlyGridItemSx}>
                            {activeType && (
                                <Box sx={detailCardSx}>
                                    {/* Every type's details are in the HTML (search engines read them all); only the
                                        active one shows, laid out exactly as before (`display: contents`). */}
                                    {types.map((type, idx) => (
                                        <Box key={type.id} sx={type.id === activeType.id ? shownPanelSx : hiddenPanelSx}>
                                            {!imageRight && (
                                                <Typography component="p" className="font-headers" sx={detailNumberSx}>
                                                    {String(idx + 1).padStart(2, '0')}
                                                </Typography>
                                            )}
                                            <Typography
                                                component="h3"
                                                className="font-headers"
                                                sx={imageRight ? narrowDetailTitleSx : detailTitleSx}
                                            >
                                                {type.title}
                                            </Typography>

                                            {renderDetailBody(type, calloutLabel, imageRight)}
                                        </Box>
                                    ))}

                                    <Box sx={progressTrackSx}>
                                        {/* Keyed on the active type so switching restarts the
                                            fill — and therefore the dwell timer — from zero. */}
                                        <Box
                                            key={activeType.id}
                                            sx={progressFillSx(autoplay, paused, ((activeIndex + 1) / types.length) * 100)}
                                            onAnimationEnd={autoplay ? showNext : undefined}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </Grid>
                    </Grid>
                </Box>
            </MUIContainer>
        </Box>
    );
};

export default memo(MvpTypes);
